import React, { useMemo, useState } from "react";
import { Search, ArrowUpDown } from "lucide-react";

const MONTH_NAMES = {
  "01": "Jan", "02": "Feb", "03": "Mar", "04": "Apr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Aug",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dec",
};

// AED, no dollar sign — shown as "AED 12,345"
const CURRENCY = new Intl.NumberFormat("en-AE", {
  style: "currency",
  currency: "AED",
  currencyDisplay: "code",
  maximumFractionDigits: 0,
});

const BAR_COLOR = "#3b6e64"; // deep pine 
const CARD_HEIGHT = 460;

export default function ExpenseAnalysis({ data = [] }) {
  
  // 1. Automatically find all "YYYY-MM" columns in the raw data
  const availableMonths = useMemo(() => {
    const monthsSet = new Set();
    data.forEach(row => {
      Object.keys(row).forEach(key => {
        // Regex looks for "YYYY-MM" format (e.g., "2026-05")
        if (/^\d{4}-\d{2}$/.test(key)) {
          monthsSet.add(key);
        }
      });
    });
    return Array.from(monthsSet).sort(); 
  }, [data]);

  // 2. Default to whichever entry matches today's month/year, 
  // or fall back to the most recent month available.
  const initialIndex = useMemo(() => {
    if (availableMonths.length === 0) return 0;
    const now = new Date();
    const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const match = availableMonths.indexOf(currentKey);
    return match !== -1 ? match : availableMonths.length - 1;
  }, [availableMonths]);

  const [monthIndex, setMonthIndex] = useState(initialIndex);
  const [query, setQuery] = useState("");
  const [sortDesc, setSortDesc] = useState(true);

  // Get the string key for the currently selected month (e.g. "2026-06")
  const activeMonthKey = availableMonths[monthIndex];

  // 3. Transform the raw GL row data into standard chart rows
  const rows = useMemo(() => {
    if (!activeMonthKey || !data.length) return [];

    let list = [];
    data.forEach((item) => {
      const val = item[activeMonthKey];
      // Only include valid numbers greater than 0
      if (typeof val === "number" && val > 0) {
        list.push({
          name: item.accdesc,
          value: val,
        });
      }
    });

    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((r) => r.name.toLowerCase().includes(q));
    }

    list.sort((a, b) => (sortDesc ? b.value - a.value : a.value - b.value));
    return list;
  }, [data, activeMonthKey, query, sortDesc]);

  const maxValue = useMemo(() => {
    if (!rows.length) return 1;
    return Math.max(...rows.map((r) => r.value));
  }, [rows]);

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + r.value, 0),
    [rows]
  );

  // Fallback if the data array is completely empty
  if (!activeMonthKey) {
    return (
      <div 
        className="w-full max-w-2xl mx-auto bg-[#fbfaf7] border border-[#e3ddd0] rounded-2xl shadow-sm flex items-center justify-center" 
        style={{ height: CARD_HEIGHT }}
      >
        <p className="text-[#8a8172]">No valid month data found.</p>
      </div>
    );
  }

  // Parse strings for the UI (e.g. "2026-06" -> "Jun 2026")
  const [activeYear, activeMonthNum] = activeMonthKey.split("-");
  const activeMonthName = MONTH_NAMES[activeMonthNum];

  return (
    <div
      className="w-full max-w-2xl mx-auto bg-[#fbfaf7] border border-[#e3ddd0] rounded-2xl shadow-sm flex flex-col overflow-hidden"
      style={{ height: CARD_HEIGHT }}
    >
      {/* Header / month selector */}
      <div className="px-6 pt-6 pb-4 border-b border-[#e3ddd0] shrink-0">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#8a8172] font-medium">
              Monthly expenses
            </p>
            <h2 className="text-2xl font-semibold text-[#2b2a26] mt-1">
              {activeMonthName} {activeYear}
            </h2>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-[#8a8172] font-medium">
              Total
            </p>
            <p className="text-xl font-semibold text-[#2b2a26] mt-1">
              {CURRENCY.format(total)}
            </p>
          </div>
        </div>

        {/* Month scrubber */}
        <div className="flex gap-1 mt-4 overflow-x-auto pb-1">
          {availableMonths.map((mKey, i) => {
            const [yyyy, mm] = mKey.split("-");
            return (
              <button
                key={mKey}
                onClick={() => setMonthIndex(i)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  i === monthIndex
                    ? "bg-[#3b6e64] text-white"
                    : "bg-[#efece3] text-[#5c584d] hover:bg-[#e3ddd0]"
                }`}
              >
                {MONTH_NAMES[mm]} '{yyyy.slice(2)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Controls: search + sort */}
      <div className="px-6 py-3 flex items-center gap-3 border-b border-[#e3ddd0] shrink-0">
        <div className="relative flex-1">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a39c8c]"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter categories…"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#f2efe8] text-sm text-[#2b2a26] placeholder-[#a39c8c] outline-none focus:ring-2 focus:ring-[#3b6e64] focus:ring-offset-1 focus:ring-offset-[#fbfaf7]"
          />
        </div>
        <button
          onClick={() => setSortDesc((s) => !s)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f2efe8] text-sm font-medium text-[#5c584d] hover:bg-[#e3ddd0] transition-colors shrink-0"
          title="Toggle sort order"
        >
          <ArrowUpDown size={14} />
          {sortDesc ? "High to low" : "Low to high"}
        </button>
      </div>

      {/* Bar list */}
      
      <div className="px-6 py-4 flex-1 min-h-0 overflow-y-auto">
        {rows.length === 0 ? (
          <p className="text-sm text-[#8a8172] py-8 text-center">
            {query
              ? `No categories match "${query}".`
              : "No expenses recorded this month."}
          </p>
        ) : (
          <ul className="space-y-3">
            {rows.map((r) => {
              const pct = Math.max((r.value / maxValue) * 100, 2);
              return (
                <li key={r.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-[#2b2a26] truncate pr-2">
                      {r.name}
                    </span>
                    <span className="text-sm tabular-nums text-[#5c584d] shrink-0">
                      {CURRENCY.format(r.value)}
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-[#efece3] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: BAR_COLOR,
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-[#e3ddd0] text-xs text-[#a39c8c] shrink-0">
        {rows.length} {rows.length === 1 ? "category" : "categories"} shown
        {query ? ` (filtered)` : ""}
      </div>
    </div>
  );
}